export interface VariationClaimPreviewItem {
  id: string;
  variation_date: string;
  total?: number | null;
  discount_percent?: number | null;
  approved_amount?: number | null;
  debit_credit?: string | null;
  hire_items?: unknown;
  hire_items_total?: number | null;
}

interface VariationClaimPreviewParams<
  TVariation extends VariationClaimPreviewItem,
> {
  variations: TVariation[];
  selectedVariationIds: Iterable<string>;
  includeVariations: boolean;
  claimMonth: number;
  claimYear: number;
  hireSourceVariationIds?: Iterable<string>;
}

export interface VariationClaimPreview {
  thisMonth: number;
  outstanding: number;
}

function getHireItems(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getVariationPeriod(
  variationDate: string,
): { month: number; year: number } | null {
  const match = /^(\d{4})-(\d{2})-\d{2}/.exec(variationDate);
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
  };
}

function getWorksAmounts(
  variation: VariationClaimPreviewItem,
  hireSourceVariationIds: ReadonlySet<string>,
): { worksRawTotal: number; worksApproved: number } {
  const rawTotal = Number(variation.total || 0);
  const hireItemsTotal = Number(variation.hire_items_total || 0);
  const hasHire =
    hireSourceVariationIds.has(variation.id) ||
    getHireItems(variation.hire_items).length > 0 ||
    hireItemsTotal > 0;
  const worksRawTotal =
    hasHire && hireItemsTotal > 0
      ? Math.max(0, rawTotal - hireItemsTotal)
      : rawTotal;
  const approvedAmount = Number(variation.approved_amount || 0);
  const worksApproved = hasHire
    ? Math.min(approvedAmount, worksRawTotal)
    : approvedAmount;
  return { worksRawTotal, worksApproved };
}

export function calculateVariationClaimContribution(
  variation: VariationClaimPreviewItem,
  hireSourceVariationIds: ReadonlySet<string> = new Set(),
): number {
  const { worksRawTotal, worksApproved } = getWorksAmounts(
    variation,
    hireSourceVariationIds,
  );
  const discountPercent = Number(variation.discount_percent || 0);
  const effectiveTotal = worksRawTotal * (1 - discountPercent / 100);
  const waitingAmount = Math.max(0, effectiveTotal - worksApproved);
  const sign = variation.debit_credit === "credit" ? -1 : 1;

  return waitingAmount < 0.01 ? 0 : waitingAmount * sign;
}

export interface PaidVariationItem extends VariationClaimPreviewItem {
  status?: string | null;
  reconciliation_status?: string | null;
}

/**
 * Mirror of the edge function's paid-variation set (isPaidVariation in
 * generate-progress-claim/index.ts): reconciliation status paid, fully
 * certified (works approved == works total), never invalid. Summed with the
 * hire-stripped approved amount and credit sign so the preview's PaidVar / A
 * match the generated PDF.
 *
 * INPUT CONTRACT: `variations` must already exclude any row flagged as removed
 * from Dashpivot (claims_variations.dashpivot_removed_at). This function does
 * NOT filter, deliberately: the engine side filters at its query, so filtering
 * again here would be redundant, and this sums money already paid, where
 * dropping a row would understate a real payment rather than prevent one.
 *
 * The safety is therefore a property of the CALLER, not of this function. The
 * only caller is ProgressClaimModal, which receives ProjectDetail's
 * `claimableVariations` (built with excludeRemovedFromDashpivot). A new caller
 * passing an unfiltered list would silently break preview-versus-PDF parity, so
 * src/lib/dashpivotRemoval.test.ts pins that every caller is pre-filtered.
 */
/**
 * PaidVar for the claim preview: the CERTIFIED variation value, mirroring
 * certifiedVariationAmount in the claim engine. It is called PaidVar for
 * historical reasons; the rule is certified, not paid.
 *
 * Josh, 24 September 2026: "change variations total included from the paid ones
 * to total approved". A is what has been claimed; payment belongs to B. This
 * used to require reconciliation_status 'paid' AND a fully approved docket,
 * which made the modal's headline disagree with the generated claim.
 */
export function calculatePaidVariationsTotal(
  variations: readonly PaidVariationItem[],
  hireSourceVariationIds: ReadonlySet<string> = new Set(),
): number {
  const total = variations.reduce((sum, v) => {
    if ((v.status || "").toLowerCase() === "invalid") return sum;
    // NO reconciliation_status gate and NO fully-approved gate. Certified is
    // the test, not paid. See certifiedVariationAmount in
    // supabase/functions/generate-progress-claim/certified-variation-value.ts,
    // which this mirrors; verify:paidvar-parity compares the two.
    const { worksRawTotal, worksApproved } = getWorksAmounts(
      v,
      hireSourceVariationIds,
    );
    const approved = Math.min(Math.max(worksApproved, 0), worksRawTotal);
    if (approved <= 0) return sum;
    const sign = v.debit_credit === "credit" ? -1 : 1;
    return sum + approved * sign;
  }, 0);
  return Math.round((total + Number.EPSILON) * 100) / 100;
}

export function calculateVariationClaimPreview<
  TVariation extends VariationClaimPreviewItem,
>({
  variations,
  selectedVariationIds,
  includeVariations,
  claimMonth,
  claimYear,
  hireSourceVariationIds = [],
}: VariationClaimPreviewParams<TVariation>): VariationClaimPreview {
  if (!includeVariations) {
    return { thisMonth: 0, outstanding: 0 };
  }

  const selectedIds = new Set(selectedVariationIds);
  const hireSourceIds = new Set(hireSourceVariationIds);

  return variations.reduce<VariationClaimPreview>(
    (totals, variation) => {
      if (!selectedIds.has(variation.id)) return totals;

      const period = getVariationPeriod(variation.variation_date);
      if (!period) return totals;

      const contribution = calculateVariationClaimContribution(
        variation,
        hireSourceIds,
      );

      if (period.year === claimYear && period.month === claimMonth) {
        totals.thisMonth += contribution;
      } else if (
        period.year < claimYear ||
        (period.year === claimYear && period.month < claimMonth)
      ) {
        totals.outstanding += contribution;
      }

      return totals;
    },
    { thisMonth: 0, outstanding: 0 },
  );
}
